<style lang="scss" src="./create-resident-drawer.scss"></style>

<template>
  <ADrawer
    :width="800"
    :open="open"
    class="manager-resident-create-drawer"
    :header-style="{ padding: '32px 40px' }"
    :body-style="{ padding: '40px' }"
    :footer-style="{ textAlign: 'left', padding: '20px 40px 40px 40px' }"
    @close="handleClose"
  >
    <template #title>
      <span class="manager-resident-create-drawer__title">Добавление Резидента</span>
    </template>

    <template #closeIcon>
      <span class="manager-resident-create-drawer__close-trigger">
        <CloseOutlined />
      </span>
    </template>

    <AForm ref="formRef" layout="vertical" :model="form" :rules="rules">
      <AFormItem class="stc-ant-field-skin" label="Название" name="name" required>
        <AInput v-model:value="form.name" placeholder="Название компании" />
      </AFormItem>

      <AFormItem class="stc-ant-field-skin" label="Кластер" name="cluster" required>
        <ASelect
          v-model:value="form.cluster"
          placeholder="Выберите кластер"
          :options="clusterOptions"
        />
      </AFormItem>

      <AFormItem class="stc-ant-field-skin" label="Email" name="email" required>
        <AInput v-model:value="form.email" placeholder="company@example.com" />
      </AFormItem>

      <AFormItem class="stc-ant-field-skin" label="ИНН" name="inn" required>
        <AInput v-model:value="form.inn" placeholder="0987654321" />
      </AFormItem>

      <AFormItem
        v-if="statusSelectOptions.length"
        class="stc-ant-field-skin"
        label="Статус"
        name="status"
      >
        <ASelect v-model:value="form.status" :options="statusSelectOptions" />
      </AFormItem>
    </AForm>

    <template #footer>
      <ARow align="middle" justify="end" class="company-editing-drawer__toolbar">
        <ACol>
          <ASpace>
            <button
              type="button"
              class="btn btn-outline btn-medium"
              :disabled="submitting"
              @click="handleClose"
            >
              Отмена
            </button>
            <button
              type="button"
              class="btn btn-primary btn-medium"
              :disabled="isSaveDisabled"
              @click="handleSubmit"
            >
              {{ submitting ? 'Сохранение...' : 'Создать' }}
            </button>
          </ASpace>
        </ACol>
      </ARow>
    </template>
  </ADrawer>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import {
  Col as ACol,
  Drawer as ADrawer,
  Form as AForm,
  FormItem as AFormItem,
  Input as AInput,
  Row as ARow,
  Select as ASelect,
  Space as ASpace,
} from 'ant-design-vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { createManagerResident } from './manager-residents-api';
import type { ManagerResidentsClusterOption, ManagerResidentStatusOption } from './types';

const props = defineProps<{
  open: boolean;
  clusters: ManagerResidentsClusterOption[];
  statusOptions: ManagerResidentStatusOption[];
}>();

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void;
  (e: 'created'): void;
}>();

const formRef = ref<FormInstance>();
const submitting = ref(false);

const form = reactive({
  name: '',
  cluster: '',
  email: '',
  inn: '',
  status: '',
});

const rules: Record<string, Rule[]> = {
  name: [{ required: true, message: 'Введите название', whitespace: true }],
  cluster: [{ required: true, message: 'Выберите кластер' }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
  inn: [{ required: true, message: 'Введите ИНН', whitespace: true }],
};

const clusterOptions = computed(() =>
  props.clusters.map((item) => ({
    value: item.id,
    label: item.name,
  })),
);

const statusSelectOptions = computed(() =>
  props.statusOptions.map((item) => ({
    value: item.id,
    label: item.name,
  })),
);

const isSaveDisabled = computed(
  () =>
    submitting.value ||
    !form.name.trim() ||
    !form.cluster ||
    !form.email.trim() ||
    !form.inn.trim(),
);

watch(
  () => props.open,
  (value) => {
    if (!value) return;
    if (!form.status && statusSelectOptions.value.length) {
      form.status = String(statusSelectOptions.value[0].value);
    }
  },
);

function resetForm(): void {
  form.name = '';
  form.cluster = '';
  form.email = '';
  form.inn = '';
  form.status = statusSelectOptions.value.length ? String(statusSelectOptions.value[0].value) : '';
  nextTick(() => {
    formRef.value?.clearValidate();
  });
}

function handleClose(): void {
  emit('update:open', false);
  resetForm();
}

async function handleSubmit(): Promise<void> {
  if (!formRef.value) return;

  try {
    await formRef.value.validate();
  } catch {
    return;
  }

  submitting.value = true;
  try {
    const msg = await createManagerResident({
      name: form.name.trim(),
      cluster: String(form.cluster),
      email: form.email.trim(),
      inn: form.inn.trim(),
      status:
        form.status ||
        (statusSelectOptions.value[0]?.value
          ? String(statusSelectOptions.value[0].value)
          : 'draft'),
    });
    showStcSuccessNotification(msg || 'Резидент успешно сохранён');
    emit('created');
    handleClose();
  } catch (error) {
    showStcErrorNotification(
      error instanceof Error ? error.message : 'Не удалось создать резидента',
    );
  } finally {
    submitting.value = false;
  }
}
</script>
